import { cn } from "@/lib/utils";

const TINTS = ["#E4E2D8", "#DDE5DE", "#E6E1E8", "#DFE4EA", "#ECE4D6"];

export const Avatar = ({ person, size = 48, className }) => {
  const style = { width: size, height: size, fontSize: size * 0.38 };
  if (person.photo) {
    return <img src={person.photo} alt={person.first} style={style} className={cn("rounded-full object-cover shrink-0", className)} />;
  }
  const words = (person.name || `${person.first} ${person.last}`).replace("&", "").split(" ").filter(Boolean);
  const initials = (words[0][0] + (words[words.length - 1][0] || "")).toUpperCase();
  const tint = TINTS[initials.charCodeAt(0) % TINTS.length];
  return (
    <div style={{ ...style, background: tint }} className={cn("rounded-full shrink-0 flex items-center justify-center font-display text-ink-soft", className)}>
      {initials}
    </div>
  );
};

const TONES = {
  brand: "status-active",
  today: "status-attention",
  past: "status-overdue",
  rhythm: "status-rhythm",
  calm: "status-done",
  private: "status-private",
  neutral: "status-neutral",
  later: "status-neutral",
};

export const Pill = ({ tone = "neutral", children, className, ...rest }) => (
  <span className={cn("status", TONES[tone], className)} {...rest}>
    {children}
  </span>
);

export const Eyebrow = ({ children, className }) => <p className={cn("t-label", className)}>{children}</p>;

export const Card = ({ className, children, ...rest }) => (
  <div className={cn("surface", className)} {...rest}>
    {children}
  </div>
);

export const Section = ({ title, hint, children, className, ...rest }) => (
  <section className={cn("mt-14", className)} {...rest}>
    <h2 className="t-heading">{title}</h2>
    {hint && <p className="t-meta mt-1">{hint}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

export const fullName = (p) => p.name || `${p.first} ${p.last}`;

export const lastText = (p) => `Last connected: ${p.lastSeen.replace(/([^,]) (\d+ days ago|yesterday|today)$/, "$1, $2")}`;

export const nextText = (p) => {
  const { tone, due } = p.next;
  if (p.status === "paused") return "Not in routine reminders";
  if (tone === "today") return "Follow-up due today";
  if (tone === "past") return `Follow-up ${due.replace(/^Was/, "was")}`;
  if (tone === "rhythm") return "Ready for a check-in";
  return `Next check-in ${due.startsWith("In ") ? due.toLowerCase() : due}`;
};

export const householdLine = (p) => {
  const m = p.household.members;
  return p.household.summary || (m.length ? `Lives with ${m.map((x) => x.name.split(" ")[0]).join(" and ")}` : "Lives alone");
};

const ACTION_TONES = ["today", "past", "rhythm"];

export const PersonLine = ({ p, why, last, next, owner, showOwner, onOpen, right, testid, avatar }) => (
  <div className="flex items-start gap-4 py-5">
    <button onClick={onOpen || undefined} disabled={!onOpen} className="flex items-start gap-4 flex-1 min-w-0 text-left group disabled:cursor-default" data-testid={testid}>
      {avatar || <Avatar person={p} size={52} />}
      <div className="min-w-0 flex-1">
        <p className="t-name group-enabled:group-hover:text-brand transition-colors">{fullName(p)}</p>
        {why && <p className="t-body mt-1">{why}</p>}
        {last && <p className="t-meta mt-0.5">{last}</p>}
        {(next || showOwner) && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2.5">
            {next && (ACTION_TONES.includes(next.tone) ? <Pill tone={next.tone}>{next.text}</Pill> : <span className="t-meta">{next.text}</span>)}
            {showOwner && (owner ? <span className="owner text-sm">Cared for by <b>{owner}</b></span> : <span className="status status-attention">No caregiver yet</span>)}
          </div>
        )}
      </div>
    </button>
    {right && <div className="pt-1">{right}</div>}
  </div>
);
